import { ChangeDetectorRef, Component } from '@angular/core';
import { AppService } from '../../app.service';
import { catchError, finalize, tap } from 'rxjs';
import { isArrayWithData } from '../../utilities/array.helpers';
import { WebhookSettingsService } from '../../services/webhook-settings/webhook-settings.service';
import { DropdownOption } from '../../interfaces/dropdown-option.interface';
import { isValidWebsite } from '../../utilities/validation.helpers';

interface WebhookSettings {
    webhookHandlerSettingId: number;
    creditorId: number;
    operationType: number | string;
    eventStatus: string;
    endpointUri: string;
    payloadContentType: number | string;
    batchIntervalInSeconds: number;
    batchSize: number;
    directTransfer: boolean;
    sendMessageContent: boolean;
    maxRetryEvents: number;
    isEnabled: boolean;
}
const INITIAL_FORM_DATA = {
    webhookHandlerSettingId: 0,
    creditorId: 0,
    operationType: 0,
    eventStatus: '',
    endpointUri: '',
    payloadContentType: 0,
    batchIntervalInSeconds: 0,
    batchSize: 0,
    directTransfer: false,
    sendMessageContent: false,
    maxRetryEvents: 0,
    isEnabled: false,
};

const VALIDATION_ERRORS = {
    endpointUri: '',
};

@Component({
    selector: 'app-crud-webhook-settings',
    templateUrl: './crud-webhook-settings.component.html',
    styleUrl: './crud-webhook-settings.component.css',
})
export class CrudWebhookSettingsComponent {
    isSideBarVisible: boolean = false;
    isDeleteModalVisible: boolean = false;
    toDeleteItemId?: number;
    editedId: number = 0;

    selectedCreditorId: string = '';
    creditorCompanyName: string = '';
    webhookSettingsList!: WebhookSettings[];

    payload: WebhookSettings = INITIAL_FORM_DATA;

    selectButtonOptions: DropdownOption[] = [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
    ];
    operationTypes!: DropdownOption[];
    contentTypes!: DropdownOption[];
    validationErrors: any = {};
    fromDate: Date = new Date();
    toDate: Date = new Date();

    public constructor(
        private appService: AppService,
        private apiService: WebhookSettingsService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.getOperationTypes();
        this.getContentTypes();
    }

    get hasTableData(): boolean {
        return isArrayWithData(this.webhookSettingsList);
    }

    getOperationTypes() {
        this.appService.isShowPageSpinner = true;
        this.apiService
            .getOperationTypes()
            .pipe(
                tap((res: any) => {
                    if (!!res && Array.isArray(res) && res.length > 0) {
                        this.operationTypes = res;
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.appService.isShowPageSpinner = false;
                }),
            )
            .subscribe();
    }

    getContentTypes() {
        this.appService.isShowPageSpinner = true;
        this.apiService
            .getContentTypes()
            .pipe(
                tap((res: any) => {
                    if (!!res && Array.isArray(res) && res.length > 0) {
                        this.contentTypes = res;
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.appService.isShowPageSpinner = false;
                }),
            )
            .subscribe();
    }

    onSelectCreditor(event: any) {
        this.selectedCreditorId = event.CreditorId;
        this.creditorCompanyName = event.CompanyName;

        this.search();
    }

    search() {
        if (!this.selectedCreditorId) {
            return;
        }

        this.webhookSettingsList = [];
        this.appService.isShowPageSpinner = true;
        this.apiService
            .get(this.selectedCreditorId)
            .pipe(
                tap((res: any) => {
                    if (!!res && Array.isArray(res) && res.length > 0) {
                        this.webhookSettingsList = res;
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.appService.isShowPageSpinner = false;
                }),
            )
            .subscribe();
    }

    onOpenSideBar(payload?: WebhookSettings) {
        if (!!this.selectedCreditorId) {
            this.validationErrors = JSON.parse(
                JSON.stringify(VALIDATION_ERRORS),
            );
            let operationType = 0;
            let payloadContentType = 0;

            if (payload) {
                operationType = +this.operationTypes.find(
                    (i: DropdownOption) => i.label === payload?.operationType,
                )?.value!;

                payloadContentType = +this.contentTypes.find(
                    (i: DropdownOption) =>
                        i.label === payload?.payloadContentType,
                )?.value!;
            } else {
                payload = INITIAL_FORM_DATA;
            }

            this.payload = {
                ...payload,
                operationType,
                payloadContentType,
            };
            this.isSideBarVisible = true;
        } else {
            this.appService.showSelectCreditorMessage();
        }
    }

    validateForm() {
        const isValidEndpointUri = isValidWebsite(this.payload.endpointUri);
        if (!isValidEndpointUri) {
            this.validationErrors.endpointUri =
                'Please input a valid endpoint uri.';
        }
    }

    get isAllValidationErrorsEmpty() {
        return Object.values(this.validationErrors).every(
            (value) => value === '',
        );
    }

    alertMessageAndSearch(action: string = 'created') {
        setTimeout(() => {
            this.appService.showCommonMessageDialog(
                'Success',
                'Webhook Settings has been successfully ' + action + '.',
            );
            this.isSideBarVisible = false;
            this.search();
        }, 5000);
    }

    updateRowFromPayload() {
        const operationType = this.operationTypes.find(
            (i: DropdownOption) => i.value === this.payload.operationType,
        )?.label!;

        const payloadContentType = this.contentTypes.find(
            (i: DropdownOption) => i.value === this.payload.payloadContentType,
        )?.label!;

        const payload = {
            ...this.payload,
            operationType,
            payloadContentType,
        };

        const newWebhookSettings = this.webhookSettingsList.map(
            (item: WebhookSettings) => {
                if (
                    item.webhookHandlerSettingId ==
                    this.payload.webhookHandlerSettingId
                ) {
                    return {
                        ...payload,
                    };
                }
                return item;
            },
        );
        this.webhookSettingsList = newWebhookSettings;
        this.cdr.detectChanges();
        this.isSideBarVisible = false;
        this.appService.isShowPageSpinner = false;
    }

    onSave() {
        this.validateForm();

        if (this.isAllValidationErrorsEmpty) {
            this.appService.isShowPageSpinner = true;
            if (
                !this.payload.webhookHandlerSettingId &&
                !!this.selectedCreditorId
            ) {
                this.apiService
                    .update(this.selectedCreditorId, this.payload)
                    .pipe(
                        tap((res: any) => {
                            this.alertMessageAndSearch();
                        }),
                        catchError(this.appService.handleError),
                        finalize(() => {}),
                    )
                    .subscribe();
            } else {
                this.apiService
                    .update(this.selectedCreditorId, this.payload)
                    .pipe(
                        tap(() => {
                            this.alertMessageAndSearch('updated');
                            //this.updateRowFromPayload();
                        }),
                        catchError(this.appService.handleError),
                        finalize(() => {}),
                    )
                    .subscribe();
            }
        } else {
            this.appService.showCommonMessageDialog(
                'Invalid',
                'Please resolve all validation errors first.',
            );
        }
    }
}
